import React from 'react';

import CircularProgress from '@mui/material/CircularProgress';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

import InitialAvatar from 'components/Avatar/InitialAvatar';
import HighlightedText from 'components/HighlightedText/HighlightedText';

import {
  memberLabel,
  PickerCategory,
  PickerGroup,
  PickerItemKind,
  PickerNavKind,
  PickerOption,
  PickerUser,
} from './types';

import {
  LoadingRow,
  NavOptionRow,
  OptionCheckbox,
  OptionChevron,
  OptionIcon,
  OptionMeta,
  OptionName,
  OptionRow,
  OptionText,
} from './styles';

type RowProps = React.HTMLAttributes<HTMLLIElement>;

const rowCheckbox = (checked: boolean): React.ReactElement => (
  <OptionCheckbox
    checked={checked}
    checkedIcon={<CheckBoxIcon fontSize="small" />}
    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
  />
);

const CategoryRow: React.FunctionComponent<RowProps & { category: PickerCategory }> = ({ category, ...rowProps }) => {
  const isUsers = category === PickerCategory.Users;
  return (
    <OptionRow {...rowProps}>
      <OptionIcon>{isUsers ? <PersonOutlineIcon /> : <GroupsOutlinedIcon />}</OptionIcon>
      <OptionText>
        <OptionName>{isUsers ? 'Users' : 'Groups'}</OptionName>
      </OptionText>
      <OptionChevron>
        <ChevronRightIcon fontSize="small" />
      </OptionChevron>
    </OptionRow>
  );
};

const BackRow: React.FunctionComponent<RowProps> = (rowProps) => (
  <NavOptionRow {...rowProps}>
    <ArrowBackIcon fontSize="small" />
    <OptionName>Back</OptionName>
  </NavOptionRow>
);

const LoadingOptionRow: React.FunctionComponent<RowProps> = (rowProps) => (
  <LoadingRow {...rowProps}>
    <CircularProgress size={16} />
    <OptionName>Loading…</OptionName>
  </LoadingRow>
);

const EmptyRow: React.FunctionComponent<RowProps & { message: string }> = ({ message, ...rowProps }) => (
  <NavOptionRow {...rowProps}>
    <OptionName>{message}</OptionName>
  </NavOptionRow>
);

const GroupRow: React.FunctionComponent<RowProps & { group: PickerGroup; query: string; checked: boolean }> = ({
  group,
  query,
  checked,
  ...rowProps
}) => (
  <OptionRow {...rowProps} aria-label={`${group.name}, ${memberLabel(group.memberCount)}`}>
    {rowCheckbox(checked)}
    <OptionText>
      <OptionName>
        <HighlightedText text={group.name} query={query} />
      </OptionName>
      <OptionMeta>{memberLabel(group.memberCount)}</OptionMeta>
    </OptionText>
  </OptionRow>
);

const UserRow: React.FunctionComponent<RowProps & { user: PickerUser; query: string; checked: boolean }> = ({
  user,
  query,
  checked,
  ...rowProps
}) => (
  <OptionRow {...rowProps}>
    {rowCheckbox(checked)}
    <InitialAvatar name={user.name} size="small" />
    <OptionText>
      <OptionName>
        <HighlightedText text={user.name} query={query} />
      </OptionName>
      {user.email ? (
        <OptionMeta>
          <HighlightedText text={user.email} query={query} />
        </OptionMeta>
      ) : null}
    </OptionText>
  </OptionRow>
);

const assertNever = (option: never): never => {
  throw new Error(`Unhandled picker option kind: ${JSON.stringify(option)}`);
};

interface PickerOptionRowProps extends RowProps {
  option: PickerOption;
  query: string;
  checked: boolean;
}

const PickerOptionRow: React.FunctionComponent<PickerOptionRowProps> = ({ option, query, checked, ...rowProps }) => {
  switch (option.kind) {
    case PickerNavKind.Category:
      return <CategoryRow {...rowProps} category={option.category} />;
    case PickerNavKind.Back:
      return <BackRow {...rowProps} />;
    case PickerNavKind.Loading:
      return <LoadingOptionRow {...rowProps} />;
    case PickerNavKind.Empty:
      return <EmptyRow {...rowProps} message={option.message} />;
    case PickerItemKind.Group:
      return <GroupRow {...rowProps} group={option} query={query} checked={checked} />;
    case PickerItemKind.User:
      return <UserRow {...rowProps} user={option} query={query} checked={checked} />;
    default:
      return assertNever(option);
  }
};

export default PickerOptionRow;
